import React, { useState, useCallback, useEffect } from 'react';
import {
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    Grid,
    CircularProgress,
    TableContainer,
    Checkbox,
    Button
} from '@material-ui/core';
import TablePager from '../../../../components/atom/TablePager';
import ActionIconButton from '../../../../components/atom/ActionIconButton';
import { useModals, MODAL_TYPE } from '../../../../hooks/useModal';
import { useNotify } from '../../../../hooks/useNotify';
import { doSwaggerCall, useSwaggerClient } from '../../../../hooks/useSwaggerClient';
import AdminHeader from '../../../../components/atom/AdminHeader';
import { EditIcon } from '../../../../icons';
import GlcodeRenderer from '../../../../components/glcode/GlcodeRenderer';
import { useHistory } from 'react-router';

function PaymentMethodsList({ clientId }) {
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const { showModal } = useModals();
    const { notifyError, notifySuccess } = useNotify();
    const history = useHistory();

    const [paymentMethods, loading, error, , reloadPaymentMethods] = useSwaggerClient(
        'clients',
        'listPaymentMethods',
        {
            clientId,
            page,
            pageSize,
            search
        },
        [page, pageSize, search]
    );
    // wire error to a notification
    useEffect(() => {
        if (error) {
            notifyError(error.message);
        }
    }, [error, notifyError]);

    const togglePaymentMethod = useCallback(
        async methodId => {
            try {
                const e = await doSwaggerCall('clients', 'getPaymentMethod', {
                    clientId,
                    methodId
                });
                await doSwaggerCall('clients', 'editPaymentMethod', {
                    clientId,
                    methodId,
                    newPaymentMethod: {
                        ...e,
                        enabled: !e.enabled
                    }
                });
                notifySuccess('Payment method enabled / disabled');
                reloadPaymentMethods();
            } catch (err) {
                notifyError('Cannot enable/disable payment method');
            }
        },
        [notifyError, notifySuccess, reloadPaymentMethods]
    );

    return (
        <Grid container spacing={2}>
            <AdminHeader
                title="Payment methods"
                search={search}
                onSearch={newSearch => {
                    setPage(0);
                    setSearch(newSearch);
                }}
            >
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        history.push(`/admin/client/${clientId}/paymentmethods/new`);
                    }}
                >
                    +
                </Button>
            </AdminHeader>
            <Grid item xs={12}>
                {loading ? (
                    <CircularProgress />
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Enabled</TableCell>
                                    <TableCell>Slug</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Note required</TableCell>
                                    <TableCell>Gl code</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paymentMethods?.items.map(item => (
                                    <TableRow key={item.id}>
                                        <TableCell>
                                            <Checkbox
                                                checked={item.enabled}
                                                onClick={e => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    showModal(MODAL_TYPE.CONFIRMATION, {
                                                        title: `Are you sure you want to disable / enable ${item.slug}?`,
                                                        content: '',
                                                        confirm: () => togglePaymentMethod(item.id)
                                                    });
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>{item.slug}</TableCell>
                                        <TableCell>{item.name}</TableCell>
                                        <TableCell>{item.methodtype}</TableCell>
                                        <TableCell>
                                            <Checkbox checked={item.note_required} disabled />
                                        </TableCell>
                                        <TableCell>
                                            <GlcodeRenderer glCode={item.gl_code} />
                                        </TableCell>
                                        <TableCell style={{ whiteSpace: 'nowrap' }}>
                                            <ActionIconButton
                                                title="Edit"
                                                icon={EditIcon}
                                                to={`/admin/client/${clientId}/paymentmethods/${item.id}`}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <Grid container justifyContent="flex-end">
                    <TablePager
                        total={paymentMethods?.total}
                        page={page}
                        setPage={setPage}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
}

export default PaymentMethodsList;
